import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, Chip } from "@/components/nb";
import { Search, MessageCircle, ThumbsUp, MapPin, X } from "lucide-react";

export default function CommunityBoard() {
  const [q, setQ] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [districts, setDistricts] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    api.get("/meghalaya/districts").then((r) => setDistricts(r.data)).catch(() => {});
  }, []);

  // debounce query typing
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (districtId) params.set("district_id", districtId);
    api.get(`/meghalaya/problems?${params.toString()}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [debounced, districtId]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((p) => { c[p.district_id] = (c[p.district_id] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <Container>
      <PageTitle
        eyebrow="COMMUNITY · MEGHALAYA"
        title="Real problems. Real students. Real solutions."
        sub="Search the community board for a problem you care about — or filter by your district."
      />

      {/* Search + filter */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4" data-testid="community-search-row">
        <div className="md:col-span-3 flex items-center gap-2 bg-white nb-border nb-shadow rounded-xl px-3 py-2">
          <Search className="w-4 h-4 shrink-0" />
          <input
            data-testid="community-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Try "water", "coal", "school", "road"...'
            className="flex-1 bg-transparent outline-none text-sm py-0.5"
          />
          {q && <button onClick={() => setQ("")} className="p-1 nb-hover" data-testid="community-clear-q"><X className="w-3 h-3" /></button>}
        </div>
        <div className="md:col-span-2 flex items-center gap-2 bg-white nb-border nb-shadow rounded-xl px-3 py-2">
          <MapPin className="w-4 h-4 shrink-0" />
          <select
            data-testid="community-district"
            value={districtId}
            onChange={(e) => setDistrictId(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm py-0.5"
          >
            <option value="">All 12 districts</option>
            {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {districtId && <button onClick={() => setDistrictId("")} className="p-1 nb-hover" data-testid="community-clear-district"><X className="w-3 h-3" /></button>}
        </div>
      </div>

      {/* Results */}
      <div className="font-mono-sl text-[10px] uppercase mb-2" data-testid="community-count">
        {loading ? "SEARCHING…" : `${items.length} MATCH${items.length === 1 ? "" : "ES"}`}
      </div>

      {!loading && items.length === 0 && (
        <NbCard className="!p-5">
          <div className="text-sm">
            No matches. Try a different keyword or clear the filter. Or head to any
            {" "}<Link to="/meghalaya" className="underline font-bold">district page</Link> and post the first one.
          </div>
        </NbCard>
      )}

      <div className="space-y-3" data-testid="community-list">
        {items.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.03 * i }}
          >
            <Link to={`/meghalaya/${p.district_id}`} data-testid={`community-item-${p.id}`}>
              <NbCard className="!p-4 nb-hover-lg">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="w-8 h-8 rounded-full bg-sl-primary nb-border grid place-items-center">{p.avatar_emoji || "🚀"}</div>
                  <div className="text-sm font-semibold">{p.author}</div>
                  <Chip color="tertiary">{p.district_name}</Chip>
                  <div className="ml-auto flex items-center gap-3 text-xs text-neutral-600">
                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {p.solutions_count || 0}</span>
                    <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {p.votes || 0}</span>
                  </div>
                </div>
                <div className="font-heading font-black text-lg tracking-tight">{p.title}</div>
                <p className="mt-1 text-sm text-neutral-700 line-clamp-2">{p.body}</p>
              </NbCard>
            </Link>
          </motion.div>
        ))}
      </div>
    </Container>
  );
}
